import { PropsWithChildren, useState } from "react";
import { Maximize2Icon } from "lucide-react";
import { MobileAppDialog } from "./mobile-app-dialog";
import { MobileHomePreview } from "./mobile-home-preview";
import { MobileManagedByPreview } from "./mobile-managed-by-preview";
import { PHONE_HEIGHT, PHONE_WIDTH } from "./phone-frame";
import { ScaledFrame } from "./scaled-frame";
import { BrandingPreviewValues } from "./types";
import { VCardDialog } from "./vcard-dialog";
import { VCARD_WIDTH, VCardPreview } from "./vcard-preview";
import { WebAppDialog } from "./web-app-dialog";
import { getWebLoginPreviewHeight, WebLoginPreview } from "./web-login-preview";

// Design size of the whole preview: the two phones and the VCard on the first
// row, and the web login spanning the second row
const PHONE_GAP = 48;
const SECTION_GAP = 136;
const ROW_GAP = 120;
const SECTION_TITLE_HEIGHT = 36;
const SECTION_TITLE_GAP = 20;
// Room around the sections for their hover highlight, which extends this far
// past them
const SECTION_BLEED = 32;
const CONTENT_WIDTH = PHONE_WIDTH * 2 + PHONE_GAP + SECTION_GAP + VCARD_WIDTH;
const CANVAS_WIDTH = CONTENT_WIDTH + SECTION_BLEED * 2;
const CANVAS_HEIGHT =
  (SECTION_TITLE_HEIGHT + SECTION_TITLE_GAP) * 2 +
  PHONE_HEIGHT +
  ROW_GAP +
  getWebLoginPreviewHeight(CONTENT_WIDTH) +
  SECTION_BLEED * 2;

export function BrandingPreview(values: BrandingPreviewValues) {
  const [isWebAppDialogOpen, setIsWebAppDialogOpen] = useState(false);
  const [isMobileAppDialogOpen, setIsMobileAppDialogOpen] = useState(false);
  const [isVCardDialogOpen, setIsVCardDialogOpen] = useState(false);

  return (
    <ScaledFrame width={CANVAS_WIDTH} height={CANVAS_HEIGHT}>
      <div
        className="flex flex-col"
        style={{ gap: ROW_GAP, padding: SECTION_BLEED }}
      >
        <div className="flex" style={{ gap: SECTION_GAP }}>
          <PreviewSection
            title="Mobile app"
            onExpand={() => setIsMobileAppDialogOpen(true)}
          >
            <div className="flex" style={{ gap: PHONE_GAP }}>
              <MobileHomePreview {...values} />
              <MobileManagedByPreview {...values} />
            </div>
          </PreviewSection>
          <PreviewSection
            title="VCard"
            onExpand={() => setIsVCardDialogOpen(true)}
          >
            <VCardPreview {...values} />
          </PreviewSection>
        </div>
        <PreviewSection
          title="Web app"
          onExpand={() => setIsWebAppDialogOpen(true)}
        >
          <WebLoginPreview {...values} width={CONTENT_WIDTH} />
        </PreviewSection>
      </div>
      <MobileAppDialog
        open={isMobileAppDialogOpen}
        onOpenChange={setIsMobileAppDialogOpen}
        previewValues={values}
      />
      <VCardDialog
        open={isVCardDialogOpen}
        onOpenChange={setIsVCardDialogOpen}
        previewValues={values}
      />
      <WebAppDialog
        open={isWebAppDialogOpen}
        onOpenChange={setIsWebAppDialogOpen}
        previewValues={values}
      />
    </ScaledFrame>
  );
}

function PreviewSection({
  title,
  onExpand,
  children,
}: PropsWithChildren<{
  title: string;
  /** Makes the whole section clickable, with a highlight on hover */
  onExpand?: () => void;
}>) {
  return (
    <section
      className="group relative isolate flex flex-col"
      style={{ gap: SECTION_TITLE_GAP }}
    >
      {onExpand && (
        <div className="absolute -inset-8 -z-10 rounded-3xl bg-background opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100" />
      )}
      <div
        className="flex items-center justify-between"
        style={{ height: SECTION_TITLE_HEIGHT }}
      >
        <span className="text-3xl font-semibold text-foreground">{title}</span>
        {onExpand && (
          <Maximize2Icon className="size-7 text-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100" />
        )}
      </div>
      {children}
      {onExpand && (
        <button
          type="button"
          aria-label={`Expand ${title}`}
          className="absolute -inset-8 rounded-3xl cursor-pointer outline-none focus-visible:ring-4 focus-visible:ring-ring"
          onClick={onExpand}
        />
      )}
    </section>
  );
}
